<template>
    <BaseDialog
        v-model:visible="visible"
        modal
        :closable="true"
        dismissable-mask
        class="modal-meter-edit-notes w-3/4 md:w-1/2"
        :header="t('title', { note: note })"
        :pt="{
            header: 'flex-col-reverse p-0!',
            headeractions: 'ml-auto',
            title: 'text-primary mb-4',
        }"
    >
        <div class="flex flex-col items-left w-full h-full">
            <BaseTextarea
                v-model="noteChanged"
                rows="5"
                cols="30"
                class="boder"
            />
        </div>
        <template #footer>
            <div class="flex flex-col gap-4 w-full">
                <div class="flex justify-between w-full">
                    <BaseButton
                        @click="visible = false"
                        :label="t('button_quit')"
                        text
                        severity="secondary"
                    />
                    <BaseButton
                        @click="handleSubmission"
                        :label="t('button_validate')"
                        severity="primary"
                        autofocus
                        :loading="isFetching"
                    />
                </div>
                <BaseMessage v-if="hasError" class="mt-2" severity="error">
                    {{ t("error") }}
                </BaseMessage>
            </div>
        </template>
    </BaseDialog>
</template>

<script setup lang="ts">
// ================================
// IMPORTS
// ================================
// Vue
import { useI18n } from "vue-i18n"
import { computed, ref, watch } from "vue"

// Service
import { SourceService } from "@core/services"
import { useFetchWrapper } from "@core/composables"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: ({ named }: { named: (key: string) => string }) =>
                `${named("note") ? "Modifier une note" : "Nouvelle note"}`,
            button_validate: "Valider",
            button_quit: "Annuler",
            meter_name: "Nom du compteur",
            error: "Une erreur est survenue lors de la sauvegarde de la note.",
        },
    },
})

const sourceService = new SourceService()

const emit = defineEmits(["update"])

type Props = {
    meterId: string
    noteId: string | null
    note: string | undefined
}

const props = defineProps<Props>()

const [visible] = defineModel<boolean>("visible", {
    required: true,
    default: false,
})

const noteChanged = ref<string>(props.note || "")

// ================================
// WATCHERS
// ================================
watch(
    () => props.note,
    (newVal) => {
        noteChanged.value = newVal || ""
    },
    { immediate: true }
)

const {
    fetch: fetchCreateNote,
    isFetching: isFetchingCreateNote,
    hasError: hasErrorCreateNote,
} = useFetchWrapper(async () => {
    await sourceService.createNoteBySourceId(props.meterId, noteChanged.value)
})

const {
    fetch: fetchUpdateNote,
    isFetching: isFetchingUpdateNote,
    hasError: hasErrorUpdateNote,
} = useFetchWrapper(async () => {
    await sourceService.updateNoteBySourceId(
        props.meterId,
        props.noteId!,
        noteChanged.value
    )
})

const hasError = computed(
    () => hasErrorCreateNote.value || hasErrorUpdateNote.value
)
const isFetching = computed(
    () => isFetchingCreateNote.value || isFetchingUpdateNote.value
)

const handleSubmission = async () => {
    // emit the selected usages
    if (!props.noteId) {
        await fetchCreateNote()
    } else {
        await fetchUpdateNote()
    }

    if (!hasError.value) {
        emit("update")
        visible.value = false
    }
}
</script>

<style scoped></style>
